import * as React from "react";
import {
  Edit,
  SimpleForm,
  TextInput,
  NumberInput,
  useNotify,
  useDataProvider,
  useRedirect,
  useRecordContext,
  Toolbar,
  SaveButton,
  SelectInput,
} from "react-admin";
import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import {
  AuthorSelectModal,
  SupervisorSelectModal,
  SpecialtySelectModal,
  UdcSelectModal,
} from "./shared/SelectModals";
import { TextField as MuiTextField } from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import axios from "axios";
import { useFormContext } from "react-hook-form";

const API_URL = process.env.REACT_APP_API_URL;

const MinimalFileUploadField = ({
  file,
  setFile,
}: {
  file: File | null;
  setFile: (f: File | null) => void;
}) => (
  <div style={{ margin: "16px 0" }}>
    <label>PDF file *</label>
    <input
      type="file"
      accept="application/pdf"
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        const file =
          e.target.files && e.target.files[0] ? e.target.files[0] : null;
        setFile(file);
      }}
    />
    {file && (
      <div>
        Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
      </div>
    )}
  </div>
);

const AuthorField = ({
  author,
  setAuthor,
}: {
  author: any;
  setAuthor: (a: any) => void;
}) => {
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      <MuiTextField
        label="Author"
        value={author ? author.fullName : "Select author..."}
        fullWidth
        margin="normal"
        InputProps={{ readOnly: true }}
        onClick={() => setShowModal(true)}
        sx={{ cursor: "pointer" }}
      />
      <AuthorSelectModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSelect={setAuthor}
        onCreate={setAuthor}
      />
    </>
  );
};

const SupervisorField = ({
  supervisor,
  setSupervisor,
}: {
  supervisor: any;
  setSupervisor: (a: any) => void;
}) => {
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      <MuiTextField
        label="Supervisor"
        value={supervisor ? supervisor.fullName : "Select supervisor..."}
        fullWidth
        margin="normal"
        InputProps={{ readOnly: true }}
        onClick={() => setShowModal(true)}
        sx={{ cursor: "pointer" }}
      />
      <SupervisorSelectModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSelect={setSupervisor}
        onCreate={setSupervisor}
      />
    </>
  );
};

const SpecialtyField = ({
  specialty,
  setSpecialty,
}: {
  specialty: any;
  setSpecialty: (a: any) => void;
}) => {
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      <MuiTextField
        label="Specialty"
        value={
          specialty
            ? `${specialty.name}${
                specialty.comment ? ` — ${specialty.comment}` : ""
              }`
            : "Select specialty..."
        }
        fullWidth
        margin="normal"
        InputProps={{ readOnly: true }}
        onClick={() => setShowModal(true)}
        sx={{ cursor: "pointer" }}
      />
      <SpecialtySelectModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSelect={setSpecialty}
        onCreate={setSpecialty}
      />
    </>
  );
};

const degreeChoices = [
  { id: "BACHELOR", name: "Бакалавр" },
  { id: "MASTER", name: "Магістр" },
  { id: "COURSE", name: "Курсова робота" },
];

export const WorkEdit = () => (
  <Edit>
    <WorkEditForm />
  </Edit>
);

const CustomToolbar = ({
  file,
  fileToRemove,
}: {
  file: File | null;
  fileToRemove: boolean;
}) => {
  const { formState } = useFormContext();
  const isDirty = formState.isDirty;
  const saveEnabled =
    (file && file.type === "application/pdf") || fileToRemove || isDirty;
  return (
    <Toolbar>
      <SaveButton disabled={!saveEnabled} />
    </Toolbar>
  );
};

const WorkEditForm = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const record = useRecordContext();
  const [file, setFile] = useState<File | null>(null);
  const [author, setAuthor] = useState<any>(record?.author || null);
  const [supervisor, setSupervisor] = useState<any>(record?.supervisor || null);
  const [specialty, setSpecialty] = useState<any>(record?.specialty || null);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [fileInfo, setFileInfo] = useState<{
    name: string;
    size: number;
  } | null>(
    record?.originalFileName && record?.fileSize
      ? { name: record.originalFileName, size: record.fileSize }
      : null
  );
  const [fileToRemove, setFileToRemove] = useState(false);
  const [formChanged, setFormChanged] = useState(false);

  useEffect(() => {
    setAuthor(record?.author || null);
    setSupervisor(record?.supervisor || null);
    setSpecialty(record?.specialty || null);
    setFileInfo(
      record?.originalFileName && record?.fileSize
        ? { name: record.originalFileName, size: record.fileSize }
        : null
    );
    setFileToRemove(false);
  }, [record]);

  useEffect(() => {
    // Enable Save if file is selected or any field is changed
    if (file && file.type === "application/pdf") {
      setFormChanged(true);
      return;
    }
    // Compare form fields
    if (!record) {
      setFormChanged(false);
      return;
    }
    const changed =
      author?.id !== record.author?.id ||
      supervisor?.id !== record.supervisor?.id ||
      specialty?.id !== record.specialty?.id ||
      false; // Add more field comparisons if needed
    setFormChanged(changed);
  }, [file, author, supervisor, specialty, record]);

  const handleRemoveFile = () => {
    setFileToRemove(true);
    setShowRemoveDialog(false);
    setFileInfo(null);
    setFile(null);
  };

  const handleSubmit = async (values: any) => {
    if (!record) return;
    try {
      // If file is marked for removal, call backend to delete it
      if (fileToRemove && record.filePath) {
        const token = localStorage.getItem("auth");
        await axios.delete(`${API_URL}/works/${record.id}/file`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }
      let formData: FormData | null = null;
      let updateData: any = {};
      if (file) {
        formData = new FormData();
        formData.append("topic", values.topic);
        formData.append("authorId", author?.id || "");
        formData.append("supervisorId", supervisor?.id || "");
        formData.append("year", values.year);
        formData.append("degree", values.degree);
        formData.append("specialtyId", specialty?.id || "");
        formData.append("keywords", values.keywords);
        formData.append(
          "bibliographicDescription",
          values.bibliographicDescription
        );
        formData.append("file", file);
      } else {
        updateData = {
          topic: values.topic,
          authorId: author?.id || "",
          supervisorId: supervisor?.id || "",
          year: values.year,
          degree: values.degree,
          specialtyId: specialty?.id || "",
          keywords: values.keywords,
          bibliographicDescription: values.bibliographicDescription,
        };
        // If file was removed and not replaced, clear file info fields
        if (fileToRemove) {
          updateData.filePath = "";
          updateData.originalFileName = "";
          updateData.fileSize = 0;
        }
      }
      await dataProvider.update("works", {
        id: record.id,
        data: formData || updateData,
        previousData: record,
      });
      notify("Work updated successfully");
      redirect("list", "works");
    } catch (error: any) {
      console.error("Update error:", error);
      if (error.response) {
        notify(
          `Error updating work: ${error.response.data?.error || error.message}`
        );
      } else {
        notify("Error updating work", { type: "error" });
      }
    }
  };

  if (!record) return null;

  return (
    <>
      <SimpleForm
        onSubmit={handleSubmit}
        defaultValues={record}
        toolbar={false}
      >
        {fileInfo && !fileToRemove ? (
          <Box sx={{ mb: 2 }}>
            <Box component="span" fontWeight="bold">
              File:
            </Box>{" "}
            {fileInfo.name} ({(fileInfo.size / 1024).toFixed(1)} KB)
            <Button
              variant="outlined"
              color="error"
              size="small"
              sx={{ ml: 2 }}
              onClick={() => setShowRemoveDialog(true)}
            >
              Remove
            </Button>
          </Box>
        ) : (
          <MinimalFileUploadField file={file} setFile={setFile} />
        )}
        <TextInput source="topic" label="Topic" fullWidth />
        <AuthorField author={author} setAuthor={setAuthor} />
        <SupervisorField
          supervisor={supervisor}
          setSupervisor={setSupervisor}
        />
        <NumberInput source="year" label="Year" fullWidth />
        <SelectInput
          source="degree"
          label="Degree"
          choices={degreeChoices}
          fullWidth
        />
        <SpecialtyField specialty={specialty} setSpecialty={setSpecialty} />
        <TextInput source="keywords" label="Keywords" fullWidth />
        <TextInput
          source="bibliographicDescription"
          label="Bibliographic Description"
          fullWidth
        />
        <Box sx={{ mt: 2 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!formChanged}
          >
            Save
          </Button>
        </Box>
      </SimpleForm>
      <Dialog
        open={showRemoveDialog}
        onClose={() => setShowRemoveDialog(false)}
      >
        <DialogTitle>Remove File</DialogTitle>
        <DialogContent>Are you sure you want to remove the file?</DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowRemoveDialog(false)}
            disabled={removing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemoveFile}
            color="error"
            variant="contained"
            disabled={removing}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
